/**
 * ConnectorConfigModalV2 — V2 redesign of the connector configure-and-test flow.
 *
 * Replaces v1's `?legacy=1&edit=<id>` punt. Renders the matching manifest's
 * configSchema as a typed form (text / secret / textarea / number / select),
 * loads existing values via getConnectorConfig, saves via saveConnectorConfig,
 * and validates the live wiring via testConnector — all in one modal.
 *
 * Field routing mirrors v1 ConnectorsPage.handleUpdate so the same backend
 * shape lands in the same encrypted columns + providerConfig JSONB:
 *   - explicit "providerConfig.<key>"  → providerConfig nested
 *   - secretFieldMap[<key>]            → encrypted DB column (e.g. apiKey, oauthClientSecret)
 *   - oauthFields[<key>]               → top-level keys
 *   - everything else                  → providerConfig JSONB
 *
 * Sentinel '••••••••' = "secret already saved, don't overwrite". Skipped on save.
 *
 * Props:
 *   - isOpen       : boolean
 *   - connector    : the installed PlatformConnector instance (has id, name, ...)
 *   - manifest     : the matching marketplace manifest (has configSchema)
 *   - onClose      : () => void
 *   - onSaved      : () => void   (parent should reload connectors)
 */
import React, { useEffect, useMemo, useState } from 'react';
// Phase 5B — Modal → Slideout per G378. Connector config is a dynamic
// schema-driven form (5-15 fields depending on connector) plus a
// Test Connection button — definitively slideout-shaped.
import { Slideout, Button, Pill } from './index';
import { CheckCircle2, AlertCircle, FlaskConical, Eye, EyeOff } from 'lucide-react';
import { getConnectorConfig, saveConnectorConfig, testConnector, updateConnector,
         getConnectorDirectoryScope, saveConnectorDirectoryScope, deleteConnectorDirectoryScope,
         listSecrets } from '../../services/api';

const SENTINEL = '••••••••';

/**
 * Is this schema field a credential?
 *
 * ONE predicate, used by every decision that depends on the answer — how the input renders, whether
 * a stored value may be read back, and whether a sentinel stands in for it. Previously each of those
 * asked `f.type === 'secret'` independently, and each independently got it wrong for the twelve
 * marketplace fields that declare the far more conventional `"type": "password"`: AWS secret access
 * keys, the SMTP password, ServiceNow's service-account password, the Okta token, four OAuth client
 * secrets. Those fell through to the default text input, so they were typed in the clear — and worse,
 * the load path treated them as ordinary values and rendered the SAVED credential back into a
 * visible box, where a screen share or a shoulder is enough to take it.
 *
 * `password` is the name the manifests use because it is the name everyone uses; the modal being the
 * only component that had never heard of it was the actual defect. Both spellings mean the same
 * thing here, and anything added later that means it too belongs in this one function.
 */
const isSecretField = (f) => f?.type === 'secret' || f?.type === 'password';

const OAUTH_FIELDS = new Set([
  'oauthClientId', 'oauthClientSecret', 'oauthRedirectUri',
  'oauthAuthUrl', 'oauthTokenUrl', 'oauthUserinfoUrl', 'oauthScopes',
  'serviceAccountJson', 'delegatedAdmin', 'baseUrlOverride',
]);

/* Fields whose values get routed to dedicated encrypted DB columns instead
 * of providerConfig JSONB. Mirrors v1 secretFieldMap exactly.
 *
 * apiKey now routes to the Connector entity's encrypted api_key column (via
 * the backend's ApiKeyEncryptionConverter) — NOT into ConnectorConfig.providerConfig
 * JSONB. Previously the absence of an apiKey mapping silently dumped the key
 * into providerConfig.apiKey while ConnectorService.testConnection kept reading
 * from Connector.apiKey (null), so every Anthropic/OpenAI/etc Test Connection
 * returned 401 even after Save. The backend ConnectorCredentialService.saveConfig
 * now has a matching apiKey branch that writes through to the Connector row.
 *
 * Vault: the value may also be a `vault://<backend>/<path>` reference instead of
 * the raw plaintext key. The backend resolves the reference at use time via
 * SecretManagerService.resolveIfReference — no special handling needed here. */
const SECRET_FIELD_MAP = {
  // Routes the SCIM directory token to Connector.scimBearerToken, an encrypted column.
  // Without this entry the value falls through to providerConfig — plain JSONB, no
  // encryption converter — and ScimService would read null.
  scimBearerToken: 'scimBearerToken',
  smtp_password: 'oauthClientSecret',
  smtp_username: 'oauthClientId',
  client_secret: 'oauthClientSecret',
  client_id:     'oauthClientId',
  api_token:     'oauthClientSecret',
  apiKey:        'apiKey',
};

const SECRET_FIELD_READ_MAP = {
  scimBearerToken: 'scimBearerToken',
  smtp_password: 'oauthClientSecret',
  smtp_username: 'oauthClientId',
  client_secret: 'oauthClientSecret',
  client_id:     'oauthClientId',
  api_token:     'oauthClientSecret',
  apiKey:        'apiKey',
};

/* Mirrors ConnectorService.isScimDirectoryType exactly ("scim_iga".equalsIgnoreCase). One answer
 * to "is this a SCIM directory", not two that can drift. */
const isScimDirectory = (c) => String(c?.type || '').toLowerCase() === 'scim_iga';

const ENTERPRISE_URN = 'urn:ietf:params:scim:schemas:extension:enterprise:2.0:User';

/* ── SCIM filter builder ──────────────────────────────────────────────────────────────────
 *
 * Dropdowns for everything the operator does not have to invent, and a text box only where a
 * real value has to be typed. A hand-typed expression is a small language with quoting rules,
 * and getting it slightly wrong produces an HTTP 400 from somebody else's directory — which is
 * a terrible place to discover a missing double quote. Choosing from what the directory
 * publishes makes a malformed filter structurally impossible.
 *
 * The structured choice is persisted as filter_spec (V349's own stated purpose: "so the UI can
 * round-trip what the operator chose instead of re-parsing an expression it generated"), and
 * the generated expression is persisted next to it because THAT is what goes to the directory
 * and what an auditor reads when asking why somebody was never seen.
 */
const SCIM_ATTRS = [
  { value: 'userName',                         label: 'Username',        type: 'string' },
  { value: 'name.givenName',                   label: 'First name',      type: 'string' },
  { value: 'name.familyName',                  label: 'Last name',       type: 'string' },
  { value: 'emails.value',                     label: 'Email',           type: 'string' },
  { value: 'title',                            label: 'Job title',       type: 'string' },
  { value: 'userType',                         label: 'User type',       type: 'string' },
  { value: 'active',                           label: 'Account active',  type: 'boolean' },
  { value: 'externalId',                       label: 'External ID',     type: 'string' },
  { value: `${ENTERPRISE_URN}:department`,     label: 'Department',      type: 'string' },
  { value: `${ENTERPRISE_URN}:division`,       label: 'Division',        type: 'string' },
  { value: `${ENTERPRISE_URN}:organization`,   label: 'Organization',    type: 'string' },
  { value: `${ENTERPRISE_URN}:costCenter`,     label: 'Cost centre',     type: 'string' },
  { value: `${ENTERPRISE_URN}:employeeNumber`, label: 'Employee number', type: 'string' },
];

/* RFC 7644 3.4.2.2 comparison operators, in the words of someone choosing one rather than
 * someone reading the specification. `pr` takes no value, which is why the value box
 * disappears for it rather than sitting there disabled and unexplained. */
const SCIM_OPS = [
  { value: 'eq', label: 'is',            needsValue: true  },
  { value: 'ne', label: 'is not',        needsValue: true  },
  { value: 'co', label: 'contains',      needsValue: true  },
  { value: 'sw', label: 'starts with',   needsValue: true  },
  { value: 'ew', label: 'ends with',     needsValue: true  },
  { value: 'pr', label: 'has any value', needsValue: false },
  { value: 'gt', label: 'is after',      needsValue: true  },
  { value: 'ge', label: 'is at or after',needsValue: true  },
  { value: 'lt', label: 'is before',     needsValue: true  },
  { value: 'le', label: 'is at or before',needsValue: true },
];

const attrMeta = (v) => SCIM_ATTRS.find((a) => a.value === v) || SCIM_ATTRS[0];
const opMeta   = (v) => SCIM_OPS.find((o) => o.value === v) || SCIM_OPS[0];
const emptyRule = () => ({ attr: 'userName', op: 'eq', value: '' });

/* Turn the structured choice into a SCIM filter expression.
 *
 * Values are wrapped in double quotes with any embedded quote or backslash escaped, per RFC 7644's
 * use of JSON string syntax — a surname like O"Brien or a Windows-style value must not be able to
 * terminate the string early and change the shape of the expression. Booleans are emitted bare,
 * because `active eq "true"` is a string comparison and would match nothing.
 *
 * One join for the whole set, deliberately. Mixing `and` and `or` in a flat list produces an
 * expression whose meaning depends on precedence rules the person clicking the dropdowns has not
 * been shown. "Match ALL" and "Match ANY" are unambiguous, and anything more intricate belongs in
 * a filter someone writes on purpose.
 */
function buildScimFilter(spec) {
  const rules = (spec?.rules || []).filter((r) => {
    if (!r || !r.attr || !r.op) return false;
    return opMeta(r.op).needsValue ? String(r.value ?? '').trim() !== '' : true;
  });
  if (rules.length === 0) return '';
  const parts = rules.map((r) => {
    if (!opMeta(r.op).needsValue) return `${r.attr} ${r.op}`;
    if (attrMeta(r.attr).type === 'boolean') {
      return `${r.attr} ${r.op} ${String(r.value) === 'true'}`;
    }
    const escaped = String(r.value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    return `${r.attr} ${r.op} "${escaped}"`;
  });
  return parts.join(spec?.match === 'any' ? ' or ' : ' and ');
}

const SELECT_CLASS =
  'bg-v2-surface border border-v2-border rounded-v2-md px-2 py-1.5 text-[12.5px] text-v2-ink';

function ScimFilterBuilder({ spec, onChange, disabled }) {
  const rules = spec?.rules?.length ? spec.rules : [emptyRule()];
  const match = spec?.match === 'any' ? 'any' : 'all';
  const expression = buildScimFilter({ match, rules });

  const update = (next) => onChange({ match, rules, ...next });
  const setRule = (i, patch) => update({
    rules: rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-[11.5px] text-v2-ink-secondary">
        <span>Include people where</span>
        <select className={SELECT_CLASS} value={match} disabled={disabled}
                aria-label="Match all or any condition"
                onChange={(e) => update({ match: e.target.value })}>
          <option value="all">all conditions match</option>
          <option value="any">any condition matches</option>
        </select>
      </div>

      {rules.map((rule, i) => {
        const meta = attrMeta(rule.attr);
        const op = opMeta(rule.op);
        return (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <select className={SELECT_CLASS} value={rule.attr} disabled={disabled}
                    aria-label={`Attribute for condition ${i + 1}`}
                    onChange={(e) => {
                      /* Switching between a text and a true/false attribute leaves the old value
                         meaningless — "Finance" is not a boolean. Clear it rather than carry a
                         value the new attribute cannot hold. */
                      const nextMeta = attrMeta(e.target.value);
                      setRule(i, {
                        attr: e.target.value,
                        value: nextMeta.type === meta.type ? rule.value : (nextMeta.type === 'boolean' ? 'true' : ''),
                      });
                    }}>
              {SCIM_ATTRS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>

            <select className={SELECT_CLASS} value={rule.op} disabled={disabled}
                    aria-label={`Operator for condition ${i + 1}`}
                    onChange={(e) => setRule(i, { op: e.target.value })}>
              {SCIM_OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>

            {op.needsValue && (meta.type === 'boolean' ? (
              <select className={SELECT_CLASS} value={String(rule.value || 'true')} disabled={disabled}
                      aria-label={`Value for condition ${i + 1}`}
                      onChange={(e) => setRule(i, { value: e.target.value })}>
                <option value="true">yes</option>
                <option value="false">no</option>
              </select>
            ) : (
              <input
                type="text"
                className="flex-1 min-w-[160px] bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12.5px] text-v2-ink"
                value={rule.value || ''}
                disabled={disabled}
                aria-label={`Value for condition ${i + 1}`}
                placeholder="type the value"
                onChange={(e) => setRule(i, { value: e.target.value })}
              />
            ))}

            {rules.length > 1 && (
              <Button variant="ghost" size="sm" disabled={disabled}
                      onClick={() => update({ rules: rules.filter((_, idx) => idx !== i) })}
                      title="Remove this condition">Remove</Button>
            )}
          </div>
        );
      })}

      <Button variant="ghost" size="sm" disabled={disabled}
              onClick={() => update({ rules: [...rules, emptyRule()] })}>
        Add condition
      </Button>

      <div>
        <span className="text-[10.5px] text-v2-ink-muted font-semibold uppercase tracking-wider">
          Filter sent to the directory
        </span>
        <div className="mt-1 rounded-v2-sm bg-v2-muted/40 px-2.5 py-1.5 font-mono-v2 text-[11.5px] text-v2-ink break-all min-h-[28px]">
          {expression || <span className="text-v2-ink-muted">— fill in a condition —</span>}
        </div>
        <span className="text-[10.5px] text-v2-ink-muted mt-1 block">
          Shown because this expression is what an auditor reads when asking why somebody was
          never pulled. It is generated from the choices above and cannot be edited directly.
        </span>
      </div>
    </div>
  );
}


const DEPTH_MIN = 0;
const DEPTH_MAX = 5;
const MIN_INTERVAL_MINUTES = 60;

/**
 * Directory scope — WHICH people this connector pulls into this organization.
 *
 * Deliberately saves on its OWN button rather than joining the slideout's Save. Scope is a
 * different resource with different failure modes: saving it contacts the directory to confirm it
 * can honour a filter at all, so a rejected filter would otherwise block an unrelated credential
 * rotation, and a credential failure would silently discard a filter just typed.
 *
 * The org is never sent. The backend resolves it from the caller's token exactly as the sync does,
 * so what is saved here is necessarily what the sync applies.
 */
function DirectoryScopeSection({ connector, disabled }) {
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  /* `legacyFilter` holds an expression that was stored without the structured state behind it —
     a filter written before this builder existed. It is shown rather than silently discarded,
     because quietly replacing somebody's filter is how a sync starts pulling a different set of
     people than anyone asked for. */
  const [legacyFilter, setLegacyFilter] = useState(null);
  const [scope, setScope] = useState({
    mode: 'filtered',
    filterSpec: { match: 'all', rules: [emptyRule()] },
    managerDepth: 3, syncEnabled: true, syncIntervalMinutes: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null); setSaved(false); setConfirmRemove(false);
      const res = await getConnectorDirectoryScope(connector.id);
      if (cancelled) return;
      if (res?.status === 403) {
        /* Configure is not permission-gated on this page, so someone without directory-scope
           authority can reach this section. That is not their mistake and must not present as a
           red failure — it is simply not theirs to set. Branching on the STATUS, never on the
           message text. */
        setReadOnly(true);
      } else if (res?.ok === false) {
        setError(res.error || 'The directory scope could not be read just now.');
      } else if (res?.data) {
        const d = res.data;
        setConfigured(true);
        const spec = (d.filterSpec && Array.isArray(d.filterSpec.rules) && d.filterSpec.rules.length)
          ? { match: d.filterSpec.match === 'any' ? 'any' : 'all', rules: d.filterSpec.rules }
          : { match: 'all', rules: [emptyRule()] };
        setLegacyFilter(
          (!d.filterSpec && d.scimFilter) ? d.scimFilter : null);
        setScope({
          mode: d.fullEnumeration ? 'everyone' : 'filtered',
          filterSpec: spec,
          managerDepth: d.managerDepth == null ? 3 : d.managerDepth,
          syncEnabled: d.syncEnabled !== false,
          syncIntervalMinutes: d.syncIntervalMinutes == null ? '' : String(d.syncIntervalMinutes),
        });
      } else {
        // 204 — nothing configured. Not an error: it is the "pulls everything" state.
        setConfigured(false);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [connector.id]);

  const set = (patch) => { setScope((p) => ({ ...p, ...patch })); setSaved(false); };

  const handleSaveScope = async () => {
    setBusy(true); setError(null); setSaved(false);
    try {
      const interval = String(scope.syncIntervalMinutes).trim();
      const filtered = scope.mode !== 'everyone';
      const res = await saveConnectorDirectoryScope(connector.id, {
        /* Both are sent: the EXPRESSION is what reaches the directory and what an auditor reads,
           the SPEC is what this builder reopens with. Neither is derivable from the other without
           writing a parser, which is the thing V349's filter_spec column exists to avoid. */
        scimFilter: filtered ? buildScimFilter(scope.filterSpec) : null,
        filterSpec: filtered ? scope.filterSpec : null,
        fullEnumeration: scope.mode === 'everyone',
        managerDepth: Number(scope.managerDepth),
        syncEnabled: scope.syncEnabled,
        syncIntervalMinutes: interval === '' ? null : Number(interval),
      });
      /* A rejected scope comes back 422 with a sentence written for this reader — "this directory
         reports that it does not support SCIM filtering", and so on. apiFetch surfaces that body's
         `message` as res.error, so it is shown verbatim rather than replaced by a generic failure. */
      if (!res || res.ok === false) {
        setError(res?.error || 'The directory scope could not be saved.');
        return;
      }
      setConfigured(true);
      setSaved(true);
    } catch (e) {
      setError(e?.message || 'Network error saving the directory scope.');
    } finally { setBusy(false); }
  };

  const handleRemoveScope = async () => {
    setBusy(true); setError(null); setSaved(false);
    try {
      const res = await deleteConnectorDirectoryScope(connector.id);
      if (!res || res.ok === false) {
        setError(res?.error || 'The directory scope could not be removed.');
        return;
      }
      setConfigured(false); setConfirmRemove(false); setLegacyFilter(null);
      setScope({
        mode: 'filtered',
        filterSpec: { match: 'all', rules: [emptyRule()] },
        managerDepth: 3, syncEnabled: true, syncIntervalMinutes: '',
      });
    } catch (e) {
      setError(e?.message || 'Network error removing the directory scope.');
    } finally { setBusy(false); }
  };

  const filterMissing = scope.mode === 'filtered' && !buildScimFilter(scope.filterSpec);
  /* Blank is NOT zero. Number('') is 0, so an empty box would silently save "no manager closure"
     — a real setting, chosen by accident, that quietly truncates every escalation chain. Treated
     as incomplete instead; 0 remains perfectly typeable when it is what the operator means. */
  const depthBlank = String(scope.managerDepth).trim() === '';
  const depthOutOfRange = !depthBlank
      && (Number(scope.managerDepth) < DEPTH_MIN || Number(scope.managerDepth) > DEPTH_MAX);
  const intervalTooShort = String(scope.syncIntervalMinutes).trim() !== ''
      && Number(scope.syncIntervalMinutes) < MIN_INTERVAL_MINUTES;
  const blocked = filterMissing || depthBlank || depthOutOfRange || intervalTooShort;

  return (
    <div className="rounded-v2-sm border border-v2-border-subtle bg-v2-surface p-3 space-y-3">
      <div className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Directory scope</div>

      {loading ? (
        <div className="text-[12px] text-v2-ink-muted">Reading the current scope…</div>
      ) : readOnly ? (
        <div className="text-[11.5px] text-v2-ink-muted leading-relaxed">
          Choosing who this directory pulls is set by an organization administrator. Ask one of
          them if this connector needs to bring in a different set of people.
        </div>
      ) : (
        <>
          {!configured && (
            <div className="rounded-v2-sm border border-v2-warning bg-v2-warning-soft p-2.5 text-[11.5px] text-v2-warning leading-relaxed">
              Nothing is scoped yet, so every sync pulls the <strong>entire directory</strong> and
              gives each person an identity on this platform. Choose who this organization actually
              needs below, then Save scope.
            </div>
          )}

          <fieldset className="space-y-1.5 border-0 p-0 m-0">
            <legend className="text-[11px] text-v2-ink-muted font-semibold mb-1">Who to pull</legend>
            <label className="flex items-start gap-2 text-[12.5px] text-v2-ink cursor-pointer">
              <input type="radio" name="scope-mode" className="mt-0.5" disabled={disabled || busy}
                     checked={scope.mode === 'filtered'} onChange={() => set({ mode: 'filtered' })} />
              <span>
                Only people matching a filter
                <span className="block text-[11px] text-v2-ink-muted">
                  Recommended. Nobody outside the filter is copied, and nobody is ever marked as
                  departed on a filtered sync — absence means "not asked for", not "left".
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-[12.5px] text-v2-ink cursor-pointer">
              <input type="radio" name="scope-mode" className="mt-0.5" disabled={disabled || busy}
                     checked={scope.mode === 'everyone'} onChange={() => set({ mode: 'everyone' })} />
              <span>
                Everyone in the directory
                <span className="block text-[11px] text-v2-ink-muted">
                  Choose this only if this organization genuinely needs the whole population. It is
                  also the only setting that lets a sync mark departed people as inactive.
                </span>
              </span>
            </label>
          </fieldset>

          {scope.mode === 'filtered' && (
            <div className="space-y-2">
              <span className="text-[11px] text-v2-ink-muted font-semibold block">Conditions</span>
              {legacyFilter && (
                <div className="rounded-v2-sm border border-v2-warning bg-v2-warning-soft p-2.5 text-[11px] text-v2-warning leading-relaxed">
                  This connector already has a filter that was not built here:{' '}
                  <span className="font-mono-v2 break-all">{legacyFilter}</span>. Saving replaces it
                  with the conditions below.
                </div>
              )}
              <ScimFilterBuilder
                spec={scope.filterSpec}
                disabled={disabled || busy}
                onChange={(next) => set({ filterSpec: next })}
              />
              <span className="text-[10.5px] text-v2-ink-muted block leading-relaxed">
                Saving also checks that this directory supports filtering at all — one that does not
                would ignore the filter and hand back everyone.
              </span>
              {filterMissing && (
                <span className="text-[10.5px] text-v2-warning block">
                  A filtered sync needs at least one complete condition. Fill one in, or choose
                  "Everyone in the directory".
                </span>
              )}
            </div>
          )}

          <label className="block">
            <span className="text-[11px] text-v2-ink-muted font-semibold">Manager depth</span>
            <input
              type="number" min={DEPTH_MIN} max={DEPTH_MAX} step={1}
              className="mt-1 w-24 bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
              value={scope.managerDepth}
              onChange={(e) => set({ managerDepth: e.target.value })}
              disabled={disabled || busy}
            />
            <span className="text-[10.5px] text-v2-ink-muted mt-1 block leading-relaxed">
              How many levels above the people you pulled to fetch as well. A filter picks a team;
              the manager an escalation needs is usually above it, so those people are fetched
              individually. 0 turns this off. Maximum {DEPTH_MAX} — an unbounded walk would pull the
              whole hierarchy back in.
            </span>
            {(depthOutOfRange || depthBlank) && (
              <span className="text-[10.5px] text-v2-warning mt-1 block">
                {depthBlank
                  ? `Enter a manager depth between ${DEPTH_MIN} and ${DEPTH_MAX}. Type 0 if you deliberately want no manager lookup.`
                  : `Manager depth must be between ${DEPTH_MIN} and ${DEPTH_MAX}.`}
              </span>
            )}
          </label>

          <label className="block">
            <span className="text-[11px] text-v2-ink-muted font-semibold">Sync every (minutes)</span>
            <input
              type="number" min={MIN_INTERVAL_MINUTES} step={1}
              className="mt-1 w-32 bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
              value={scope.syncIntervalMinutes}
              onChange={(e) => set({ syncIntervalMinutes: e.target.value })}
              disabled={disabled || busy}
              placeholder="on demand"
            />
            <span className="text-[10.5px] text-v2-ink-muted mt-1 block leading-relaxed">
              Leave empty to sync only when someone presses Sync from IGA. The shortest repeating
              interval is {MIN_INTERVAL_MINUTES} minutes — directories rate-limit, and a tighter
              loop spends the whole estate's quota.
            </span>
            {intervalTooShort && (
              <span className="text-[10.5px] text-v2-warning mt-1 block">
                The shortest interval is {MIN_INTERVAL_MINUTES} minutes. Leave it empty for on demand only.
              </span>
            )}
          </label>

          <label className="flex items-center gap-2 text-[12.5px] text-v2-ink cursor-pointer">
            <input type="checkbox" checked={scope.syncEnabled} disabled={disabled || busy}
                   onChange={(e) => set({ syncEnabled: e.target.checked })} />
            <span>
              Include in scheduled syncs
              <span className="block text-[11px] text-v2-ink-muted">
                Unticking pauses the repeating sync only. Sync from IGA still works when someone runs it.
              </span>
            </span>
          </label>

          {error && (
            <div className="flex items-start gap-2 rounded-v2-sm border border-v2-danger bg-v2-danger-soft p-2.5">
              <AlertCircle size={13} className="text-v2-danger flex-shrink-0 mt-0.5" />
              <div className="text-[11.5px] text-v2-danger leading-relaxed">{error}</div>
            </div>
          )}
          {saved && !error && (
            <div className="flex items-center gap-2 text-[11.5px] text-v2-success">
              <CheckCircle2 size={13} /> Scope saved. It applies from the next sync.
            </div>
          )}

          <div className="flex items-center gap-2 pt-0.5 flex-wrap">
            <Button variant="secondary" onClick={handleSaveScope}
                    loading={busy} disabled={disabled || busy || blocked}>
              {busy ? 'Saving…' : 'Save scope'}
            </Button>
            {configured && !confirmRemove && (
              <Button variant="ghost" onClick={() => setConfirmRemove(true)} disabled={disabled || busy}>
                Remove scope
              </Button>
            )}
            {configured && confirmRemove && (
              <>
                <span className="text-[11.5px] text-v2-warning">
                  Removing it means the next sync pulls everyone again. Continue?
                </span>
                <Button variant="ghost" onClick={handleRemoveScope} disabled={busy}>Remove</Button>
                <Button variant="ghost" onClick={() => setConfirmRemove(false)} disabled={busy}>Keep</Button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function ConnectorConfigModalV2({
  isOpen,
  connector,
  manifest,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({});
  const [revealed, setRevealed] = useState({});  // per-field reveal toggle for textareas
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saveError, setSaveError] = useState(null);

  /* ── Secrets Manager sourcing for credential fields ───────────────────────
   *
   * A credential field can be filled two ways: typed as plaintext, or pointed at
   * an entry that already lives in the vault. The second is what the connector
   * SHOULD carry — the backend externalizes SMTP passwords on save anyway, and a
   * reference chosen here means the plaintext never travels through this form at
   * all.
   *
   * Loaded lazily on first use rather than on open: most connectors are configured
   * with a typed credential, and listing the vault costs a round trip that the
   * common path does not need. `secretOptions === null` means "not fetched yet",
   * which is what keeps loadSecrets idempotent. */
  const [secretPickerFor, setSecretPickerFor] = useState({});
  const [secretOptions, setSecretOptions] = useState(null);
  const [secretsLoading, setSecretsLoading] = useState(false);
  const [secretsError, setSecretsError] = useState(null);

  const loadSecrets = async () => {
    if (secretOptions !== null || secretsLoading) return;
    setSecretsLoading(true);
    setSecretsError(null);
    try {
      const res = await listSecrets();
      if (res && res.ok === false) throw new Error(res.error || 'Request failed');
      setSecretOptions(res?.data?.records || []);
    } catch (e) {
      /* Empty array, not null: null would make loadSecrets retry on every render of
       * the open picker and hammer the endpoint while the backend is down. */
      setSecretOptions([]);
      setSecretsError(e?.message || 'Could not load Secrets Manager entries');
    } finally {
      setSecretsLoading(false);
    }
  };

  /* RC-74.5 — Identity edit fields (name / description / baseUrl).
   *
   * V1 lets operators rename a connector, change its description, or
   * point it at a different base URL inline from the same modal. V2
   * stripped these — the only way to change the name became to delete
   * + re-add, which is unsafe (loses bindings + secrets). We restore
   * the fields here, seeded from `connector` props on open, and save
   * via updateConnector before saveConnectorConfig in handleSave.
   *
   * State is separate from `form` (which is credentials-only) so the
   * existing payload shape into saveConnectorConfig is untouched. A
   * combined dirty flag (identityDirty || form-dirty) drives the Save
   * button. */
  const [identity, setIdentity] = useState({ name: '', description: '', baseUrl: '' });
  const [identityInitial, setIdentityInitial] = useState({ name: '', description: '', baseUrl: '' });
  useEffect(() => {
    if (!isOpen || !connector) return;
    const seed = {
      name: connector.name || '',
      description: connector.description || '',
      /* RC-74.6 — the wire field is `url` (Connector entity column `url`),
       * NOT `baseUrl`. `connector.baseUrl` is undefined on every installed
       * instance (PlatformContext flattens ConnectorResponse -> the Connector
       * entity), so this box rendered EMPTY even when a URL was saved and the
       * operator was shown "no base URL" for a connector that had one.
       * `baseUrl` exists only on the marketplace MANIFEST row, which is
       * already surfaced as this field's placeholder. V1
       * (ConnectorsPage.handleUpdate) reads and writes `url`; same contract
       * here. The local state key stays `baseUrl` because that is the UI
       * label; only the wire key is corrected. */
      baseUrl: connector.url || connector.instance?.url || '',
    };
    setIdentity(seed);
    setIdentityInitial(seed);
  }, [isOpen, connector]);
  const identityDirty = (
    identity.name !== identityInitial.name ||
    identity.description !== identityInitial.description ||
    identity.baseUrl !== identityInitial.baseUrl
  );

  /* Build a schema even if the manifest didn't ship one: fall back to a
   * minimal "API key" field so legacy custom connectors are still usable. */
  const schema = useMemo(() => {
    if (Array.isArray(manifest?.configSchema) && manifest.configSchema.length > 0) {
      return manifest.configSchema;
    }
    return [
      { field: 'apiKey', label: 'API Key', type: 'secret', required: false, encrypted: true,
        placeholder: '••••••••••••••••',
        helpText: 'Stored encrypted. The connector will send this as the Authorization header.' },
    ];
  }, [manifest]);

  /* Load existing config on open. Mirror v1's read logic so populated
   * fields show '••••••••' sentinels for write-only secrets. */
  useEffect(() => {
    if (!isOpen || !connector?.id) return;
    setLoading(true);
    setTestResult(null);
    setSaveError(null);

    /* Seed defaults first. */
    const defaults = {};
    schema.forEach((f) => { defaults[f.field] = f.defaultValue || ''; });

    getConnectorConfig(connector.id)
      .then((result) => {
        const data = result?.data;
        if (!data || data.configStatus === 'unconfigured') {
          setForm(defaults);
          return;
        }
        const pc = data.providerConfig || {};
        const loaded = { ...defaults };
        for (const f of schema) {
          const parts = f.field.split('.');
          if (parts.length > 1 && parts[0] === 'providerConfig') {
            const subKey = parts.slice(1).join('.');
            if (pc[subKey] !== undefined && pc[subKey] !== null) {
              loaded[f.field] = isSecretField(f) ? SENTINEL : String(pc[subKey]);
            }
          } else if (SECRET_FIELD_READ_MAP[f.field]) {
            const dbCol = SECRET_FIELD_READ_MAP[f.field];
            if (data[dbCol] !== undefined && data[dbCol] !== null) {
              loaded[f.field] = String(data[dbCol]);
            } else if (dbCol === 'oauthClientSecret' && data.clientSecretRef) {
              /* The secret lives in Secrets Manager and the column holds a pointer.
               * Show the pointer rather than the anonymous SENTINEL: the operator needs
               * to see WHICH entry this connector resolves against, and the reference is
               * not itself a credential. oauthClientSecret stays WRITE_ONLY — clientSecretRef
               * is a separate computed field that is null whenever the column holds a real
               * credential, so a typed password can never leak through this branch. */
              loaded[f.field] = String(data.clientSecretRef);
            } else if (isSecretField(f) && (data.hasClientSecret || data.hasApiKey || data.configStatus === 'configured')) {
              /* hasApiKey added so the slideout shows the SENTINEL after Save
               * when only the Connector entity's encrypted api_key column is
               * populated (no OAuth / providerConfig entries). Without this,
               * a freshly-saved Anthropic key would re-open the slideout with
               * an empty field and the user couldn't tell whether Save worked. */
              loaded[f.field] = SENTINEL;
            }
          } else if (OAUTH_FIELDS.has(f.field)) {
            if (data[f.field] !== undefined && data[f.field] !== null) {
              loaded[f.field] = String(data[f.field]);
            } else if (isSecretField(f) && (data.hasClientSecret || data.configStatus === 'configured')) {
              loaded[f.field] = SENTINEL;
            } else if ((f.type === 'textarea' || f.encrypted) && data.configStatus === 'configured') {
              /* G164 — long-form encrypted blobs (service-account JSON, PEM) are
               * stored write-only; show sentinel so user knows a value is saved. */
              loaded[f.field] = SENTINEL;
            }
          } else if (pc[f.field] !== undefined && pc[f.field] !== null) {
            loaded[f.field] = String(pc[f.field]);
          } else if (data[f.field] !== undefined && data[f.field] !== null) {
            loaded[f.field] = String(data[f.field]);
          }
        }
        setForm(loaded);
      })
      .catch(() => setForm(defaults))
      .finally(() => setLoading(false));
  }, [isOpen, connector?.id, schema]);

  if (!isOpen || !connector) return null;

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const validateRequired = () => {
    const missing = schema
      .filter((f) => {
        if (!f.required) return false;
        const val = form[f.field];
        if (val === SENTINEL) return false;
        return !val || (typeof val === 'string' && val.trim() === '');
      })
      .map((f) => f.label);
    return missing;
  };

  const buildBackendPayload = () => {
    const backendPayload = {};
    const providerConfig = {};
    for (const [key, value] of Object.entries(form)) {
      if (value === '' || value === undefined) continue;
      if (value === SENTINEL) continue;
      const parts = key.split('.');
      if (parts.length > 1 && parts[0] === 'providerConfig') {
        providerConfig[parts.slice(1).join('.')] = value;
      } else if (SECRET_FIELD_MAP[key]) {
        backendPayload[SECRET_FIELD_MAP[key]] = value;
      } else if (OAUTH_FIELDS.has(key)) {
        backendPayload[key] = value;
      } else {
        providerConfig[key] = value;
      }
    }
    if (Object.keys(providerConfig).length > 0) {
      backendPayload.providerConfig = providerConfig;
    }
    return backendPayload;
  };

  const handleSave = async ({ thenTest = false } = {}) => {
    const missing = validateRequired();
    if (missing.length > 0) {
      setSaveError(`Missing required fields: ${missing.join(', ')}`);
      return false;
    }
    // RC-74.5 — empty name is invalid (DB column is NOT NULL).
    if (identityDirty && !identity.name?.trim()) {
      setSaveError('Name is required.');
      return false;
    }
    setSaving(true);
    setSaveError(null);
    try {
      /* RC-74.5 — Save identity first (cheap PUT, no secret rotation).
       * If it fails, abort before touching credentials so the operator's
       * "Save" doesn't half-apply (e.g. credentials saved but name stuck). */
      if (identityDirty) {
        /* RC-74.6 — the key MUST be `url`. `Connector` is bound directly as
         * the @RequestBody of PUT /api/v1/connectors/{id} and carries no
         * @JsonIgnoreProperties(ignoreUnknown = true), so under Spring Boot
         * 3.2 (FAIL_ON_UNKNOWN_PROPERTIES defaults to true) a stray `baseUrl`
         * key aborted deserialization of the ENTIRE body. Every identity save
         * — name and description included — returned 400 "Request body could
         * not be parsed. Ensure the payload is valid JSON." for every
         * connector in every tenant. ConnectorService.updateConnector applies
         * updates.getUrl(); nothing ever read a `baseUrl`. */
        const idPatch = {
          name: identity.name.trim(),
          description: identity.description || '',
          url: identity.baseUrl || '',
        };
        const idRes = await updateConnector(connector.id, idPatch);
        if (!idRes || idRes.ok === false) {
          setSaveError(idRes?.error || idRes?.data?.error || 'Failed to save name/description/URL');
          return false;
        }
        // Re-seed initial so the dirty flag clears on the same render.
        setIdentityInitial({ ...identity });
      }
      // Credentials save only fires if there's something dirty in the form.
      const credentialsDirty = Object.values(form).some((v) => v && v !== SENTINEL);
      if (credentialsDirty) {
        const payload = buildBackendPayload();
        const result = await saveConnectorConfig(connector.id, payload);
        if (!result || result.ok === false) {
          setSaveError(result?.error || result?.data?.error || 'Save failed');
          return false;
        }
      }
      onSaved && onSaved();
      if (thenTest) await handleTest();
      return true;
    } catch (e) {
      setSaveError(e?.message || 'Network error saving configuration');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testConnector(connector.id);
      setTestResult({
        ok: !!(result && result.ok !== false),
        data: result?.data,
        error: result?.error || result?.data?.error,
        latencyMs: result?.data?.latencyMs,
        status: result?.data?.status,
      });
    } catch (e) {
      setTestResult({ ok: false, error: e?.message || 'Network error during test' });
    } finally {
      setTesting(false);
    }
  };

  const renderField = (f) => {
    const key = f.field;
    const val = form[key] ?? '';
    const isSentinel = val === SENTINEL;
    const isRevealed = !!revealed[key];

    const labelEl = (
      <label className="block text-[12px] font-medium text-v2-ink mb-1" htmlFor={`cfg-${key}`}>
        {f.label}
        {f.required && <span className="text-v2-danger ml-0.5">*</span>}
      </label>
    );

    const helpEl = f.helpText ? (
      <div className="text-[11px] text-v2-ink-muted mt-1">{f.helpText}</div>
    ) : null;

    const baseInputCls =
      'w-full text-[12.5px] font-mono-v2 px-2.5 py-1.5 rounded-v2-sm ' +
      'bg-v2-surface border border-v2-border-subtle ' +
      'focus:outline-none focus:border-v2-trust focus:ring-1 focus:ring-v2-trust v2-focus-ring';

    if (f.type === 'textarea') {
      /* G167 — controlled value ALWAYS reflects state (val). Masking is purely
       * a CSS concern; using the OS-level password-style obfuscation via the
       * webkit text-security trick would interfere with paste, so for textareas
       * we just blur the visible glyphs via filter when the value isn't revealed.
       * The actual content is preserved in state at all times. */
      const showSentinelHint = isSentinel;          // value came from server, not yet typed
      const textareaVal = isSentinel ? '' : val;
      const obfuscate = f.encrypted && !isRevealed && textareaVal && !isSentinel;
      return (
        <div key={key} className="space-y-0">
          <div className="flex items-center justify-between">
            {labelEl}
            {f.encrypted && textareaVal && (
              <button
                type="button"
                className="text-[10.5px] text-v2-trust hover:underline flex items-center gap-1"
                onClick={() => setRevealed((p) => ({ ...p, [key]: !p[key] }))}
              >
                {isRevealed ? <EyeOff size={11} /> : <Eye size={11} />}
                {isRevealed ? 'Hide' : 'Reveal'}
              </button>
            )}
          </div>
          <textarea
            id={`cfg-${key}`}
            className={baseInputCls + ' min-h-[140px] resize-y' + (obfuscate ? ' v2-textarea-mask' : '')}
            style={obfuscate ? { WebkitTextSecurity: 'disc', textSecurity: 'disc' } : undefined}
            placeholder={showSentinelHint ? 'Value saved. Paste a new value to replace it.' : (f.placeholder || 'Paste here…')}
            value={textareaVal}
            onChange={(e) => updateField(key, e.target.value)}
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="off"
          />
          {showSentinelHint && (
            <div className="text-[10.5px] text-v2-ink-muted mt-1 italic">A value is already saved. Type/paste to replace it.</div>
          )}
          {f.encrypted && textareaVal && !isRevealed && (
            <div className="text-[10.5px] text-v2-ink-muted mt-1">{textareaVal.length.toLocaleString()} characters captured · click Reveal to inspect</div>
          )}
          {helpEl}
        </div>
      );
    }

    if (isSecretField(f)) {
      /* A credential field has three states, and they need to look different —
       * conflating them is how an operator ends up unsure whether the vault was
       * actually used:
       *   1. holds a vault:// reference  → show the reference, never a password box
       *   2. picker open                 → choose an existing Secrets Manager entry
       *   3. default                     → type the credential, with an opt-in to (2)
       * The reference is deliberately shown in full. It is not a secret — it is a
       * pointer — and seeing which entry a connector points at is exactly what makes
       * a mis-wired connector diagnosable without a database query. */
      const isRef = typeof val === 'string' && val.startsWith('vault://');
      const pickerOpen = !!secretPickerFor[key];

      const openPicker = () => {
        setSecretPickerFor((p) => ({ ...p, [key]: true }));
        loadSecrets();
      };
      const closePicker = () => setSecretPickerFor((p) => ({ ...p, [key]: false }));

      if (isRef) {
        return (
          <div key={key}>
            {labelEl}
            <div className="flex items-center gap-1.5">
              <div
                className={baseInputCls + ' truncate text-v2-ink-muted'}
                title={val}
              >
                {val}
              </div>
              <button
                type="button"
                className="text-[10.5px] text-v2-trust hover:underline whitespace-nowrap"
                onClick={() => { updateField(key, ''); closePicker(); }}
              >
                Change
              </button>
            </div>
            <div className="text-[10.5px] text-v2-ink-muted mt-1">
              Held in Secrets Manager. The credential itself is never stored on the connector.
            </div>
            {helpEl}
          </div>
        );
      }

      if (pickerOpen) {
        return (
          <div key={key}>
            <div className="flex items-center justify-between">
              {labelEl}
              <button
                type="button"
                className="text-[10.5px] text-v2-trust hover:underline"
                onClick={closePicker}
              >
                Enter manually
              </button>
            </div>
            <select
              id={`cfg-${key}`}
              className={baseInputCls}
              value=""
              disabled={secretsLoading}
              onChange={(e) => {
                if (!e.target.value) return;
                updateField(key, e.target.value);
                closePicker();
              }}
            >
              <option value="">
                {secretsLoading ? 'Loading…' : '— Select a stored secret —'}
              </option>
              {(secretOptions || []).map((s) => (
                <option key={s.wireRef} value={s.wireRef}>{s.path}</option>
              ))}
            </select>
            {secretsError && (
              <div className="text-[10.5px] text-v2-danger mt-1">{secretsError}</div>
            )}
            {!secretsLoading && !secretsError && (secretOptions || []).length === 0 && (
              <div className="text-[10.5px] text-v2-ink-muted mt-1">
                No stored secrets yet. Add one in Secrets Manager, or enter the credential manually.
              </div>
            )}
            {helpEl}
          </div>
        );
      }

      return (
        <div key={key}>
          <div className="flex items-center justify-between">
            {labelEl}
            <button
              type="button"
              className="text-[10.5px] text-v2-trust hover:underline"
              onClick={openPicker}
            >
              Use a stored secret
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            <input
              id={`cfg-${key}`}
              type={isRevealed ? 'text' : 'password'}
              className={baseInputCls}
              placeholder={f.placeholder || '••••••••••••••••'}
              value={val}
              onChange={(e) => updateField(key, e.target.value)}
              autoComplete="new-password"
              spellCheck={false}
            />
            <button
              type="button"
              className="text-v2-ink-muted hover:text-v2-ink"
              onClick={() => setRevealed((p) => ({ ...p, [key]: !p[key] }))}
              aria-label={isRevealed ? 'Hide' : 'Reveal'}
            >
              {isRevealed ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          {helpEl}
        </div>
      );
    }

    if (f.type === 'select' && Array.isArray(f.options)) {
      return (
        <div key={key}>
          {labelEl}
          <select
            id={`cfg-${key}`}
            className={baseInputCls}
            value={val}
            onChange={(e) => updateField(key, e.target.value)}
          >
            <option value="">— Select —</option>
            {f.options.map((opt) => (
              <option key={opt.value || opt} value={opt.value || opt}>
                {opt.label || opt}
              </option>
            ))}
          </select>
          {helpEl}
        </div>
      );
    }

    /* default: text / number.
     *
     * Reached only by fields that are NOT credentials — isSecretField above claims every one of
     * those before control arrives here. The `type` below is therefore deliberately never
     * 'password': if a new credential spelling ever slips past the predicate it must show up as a
     * visible box in review, not quietly render masked and give the impression it was handled. */
    return (
      <div key={key}>
        {labelEl}
        <input
          id={`cfg-${key}`}
          type={f.type === 'number' ? 'number' : 'text'}
          className={baseInputCls}
          placeholder={f.placeholder || ''}
          value={val}
          onChange={(e) => updateField(key, e.target.value)}
        />
        {helpEl}
      </div>
    );
  };

  /* RC-74.5 — Combined dirty: identity edits OR credentials edits
   * both enable Save. Pre-RC-74.5 this only watched the credentials
   * form, so renames/URL changes (which are mutually exclusive from
   * credential edits in the common case) never enabled Save. */
  const dirty = identityDirty || Object.values(form).some((v) => v && v !== SENTINEL);

  return (
    <Slideout
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      width={720}
      ariaLabel={`Configure connector — ${connector.name}`}
      header={
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="text-[15px] font-bold m-0 truncate">Configure</h2>
          <span className="text-v2-ink-muted font-normal truncate text-[13px]">— {connector.name}</span>
          {connector.status && <Pill tone={connector.status === 'connected' ? 'success' : 'warning'}>{connector.status}</Pill>}
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Close</Button>
          <Button
            variant="secondary"
            iconLeft={<FlaskConical size={13} />}
            onClick={handleTest}
            loading={testing}
            disabled={saving}
          >
            {testing ? 'Testing…' : 'Test Connection'}
          </Button>
          <Button
            variant="primary"
            iconLeft={<CheckCircle2 size={13} />}
            onClick={() => handleSave({ thenTest: false })}
            loading={saving}
            disabled={!dirty}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="text-[12.5px] text-v2-ink-muted">Loading existing configuration…</div>
      ) : (
        <div className="space-y-4">
          {manifest && (
            <div className="rounded-v2-sm border border-v2-border-subtle bg-v2-muted/40 p-3 text-[11.5px] text-v2-ink-secondary leading-relaxed">
              <span className="font-semibold text-v2-ink">{manifest.name || manifest.displayName}</span>
              {' · '}
              <span>Auth: {manifest.authType || 'none'}</span>
              {manifest.baseUrl && <> · <span className="font-mono-v2">{manifest.baseUrl}</span></>}
            </div>
          )}

          {/* RC-74.5 — Identity section.
              Restores V1's inline rename / description / base URL edits.
              Pre-RC-74.5 the only way to change these was delete + re-add,
              which breaks every existing skill→connector binding and
              forces a re-grant of secrets. Sits at the top of the
              configure surface because operators usually want to confirm
              identity BEFORE rotating credentials. Saves are batched into
              the same Save button via the updated handleSave above. */}
          <div className="rounded-v2-sm border border-v2-border-subtle bg-v2-surface p-3 space-y-2.5">
            <div className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Identity</div>
            <label className="block">
              <span className="text-[11px] text-v2-ink-muted font-semibold">Name</span>
              <input
                type="text"
                className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                value={identity.name}
                onChange={(e) => setIdentity((prev) => ({ ...prev, name: e.target.value }))}
                disabled={saving}
                placeholder="e.g. Splunk · Production"
              />
            </label>
            <label className="block">
              <span className="text-[11px] text-v2-ink-muted font-semibold">Description</span>
              <textarea
                rows={2}
                className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12.5px] text-v2-ink"
                value={identity.description}
                onChange={(e) => setIdentity((prev) => ({ ...prev, description: e.target.value }))}
                disabled={saving}
                placeholder="What's this connector for? (recorded in audit trail)"
              />
            </label>
            <label className="block">
              <span className="text-[11px] text-v2-ink-muted font-semibold">Base URL</span>
              <input
                type="text"
                className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] font-mono-v2 text-v2-ink"
                value={identity.baseUrl}
                onChange={(e) => setIdentity((prev) => ({ ...prev, baseUrl: e.target.value }))}
                disabled={saving}
                placeholder={manifest?.baseUrl || 'https://api.example.com/v1'}
              />
              {manifest?.baseUrl && identity.baseUrl !== manifest.baseUrl && (
                <span className="text-[10.5px] text-v2-ink-muted mt-1 block">
                  Overrides the manifest default <span className="font-mono-v2">{manifest.baseUrl}</span>.
                </span>
              )}
            </label>
          </div>

          {schema.map(renderField)}

          {/* V349 — only for SCIM directory connectors: nothing else has a directory to scope. */}
          {isScimDirectory(connector) && (
            <DirectoryScopeSection connector={connector} disabled={saving} />
          )}

          {saveError && (
            <div className="flex items-start gap-2 rounded-v2-sm border border-v2-danger-border bg-v2-danger-soft p-3">
              <AlertCircle size={14} className="text-v2-danger flex-shrink-0 mt-0.5" />
              <div className="text-[12px] text-v2-danger leading-relaxed">{saveError}</div>
            </div>
          )}

          {testResult && (
            <div className={
              'rounded-v2-sm border p-3 ' +
              (testResult.ok
                ? 'border-v2-success-border bg-v2-success-soft'
                : 'border-v2-danger-border bg-v2-danger-soft')
            }>
              <div className="flex items-center gap-2 text-[12px] font-semibold mb-1.5">
                {testResult.ok
                  ? <CheckCircle2 size={14} className="text-v2-success" />
                  : <AlertCircle size={14} className="text-v2-danger" />}
                <span className={testResult.ok ? 'text-v2-success' : 'text-v2-danger'}>
                  {testResult.ok ? 'Connection succeeded' : 'Connection failed'}
                </span>
                {testResult.latencyMs != null && testResult.latencyMs >= 0 && (
                  <span className="text-v2-ink-muted font-normal text-[11px]">· {testResult.latencyMs} ms</span>
                )}
              </div>
              {(testResult.error || testResult.status) && (
                <div className="text-[11.5px] text-v2-ink-secondary font-mono-v2 whitespace-pre-wrap break-words">
                  {testResult.error || testResult.status}
                </div>
              )}
              {testResult.data && typeof testResult.data === 'object' && (
                <details className="mt-2">
                  <summary className="text-[10.5px] text-v2-ink-muted cursor-pointer">Raw response</summary>
                  <pre className="text-[10.5px] font-mono-v2 mt-1 max-h-[180px] overflow-auto bg-v2-muted/40 rounded p-2">
                    {JSON.stringify(testResult.data, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>
      )}
    </Slideout>
  );
}

export default ConnectorConfigModalV2;
